import { DatasetAudioRecording, TaskTypeEnum } from '@api';
import { Tooltip } from '@components/ui';
import { useApi } from '@data-access';
import { GeneralHelper } from '@helpers';
import { FileFetchError } from '@helpers/general';
import { notionStore } from '@stores/notion.store';
import clsx from 'clsx';
import DOMPurify from 'dompurify';
import { AlertTriangle, ChevronLeft, ChevronRight, FileText, HelpCircle, Info, Search, X } from 'lucide-react';
import { observer } from 'mobx-react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { AudioRecordingsContext, ResolveAudioByFileName } from './AudioRecordingsContext';
import { DocumentsContext, DocumentsMap } from './DocumentsContext';
import styles from './FilePreview.module.scss';
import { PreviewGrid } from './grid/PreviewGrid';
import { SearchContext } from './Highlight';
import { CellFullView } from './overlay/CellFullView';
import { OverlayShell } from './OverlayShell';
import { BYTE_TRIPWIRE } from './parse/limits';
import { parseCsv } from './parse/parseCsv';
import { parseJsonl } from './parse/parseJsonl';
import { RecordsView } from './records/RecordsView';
import { analyzeColumns, getRenderer, recommendLayout } from './renderers/registry';
import { ColumnAnalysis, FilePreviewProps, ParsedFile, PreviewLayout } from './types';

const PAGE_SIZE = 50;

type LoadState =
  | { status: 'loading' }
  | { status: 'unsupported' }
  | { status: 'error'; fetchStatus: number | 'toolarge' | null }
  | { status: 'ready'; parsed: ParsedFile; analyses: ColumnAnalysis[] };

const formatBytes = (bytes: number | null): string => {
  if (bytes === null) return '';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

const errorMessage = (fetchStatus: number | 'toolarge' | null): { title: string; detail: string } => {
  if (fetchStatus === 'toolarge') {
    return { title: 'This file is too large to preview', detail: 'Download it to see the contents.' };
  }
  if (fetchStatus === 401 || fetchStatus === 403) {
    return {
      title: 'This file could not be loaded',
      detail: 'The download link has expired. Reload the page to get a new one.',
    };
  }
  if (fetchStatus === 404) {
    return { title: 'This file could not be found', detail: 'It may have been moved or deleted.' };
  }
  return { title: 'This file could not be loaded', detail: 'Something went wrong while fetching it.' };
};

const infoboxKey = (taskType?: string): string | null => {
  switch (taskType) {
    case TaskTypeEnum.tabularMl:
      return 'tabularMlInputFile';
    case TaskTypeEnum.singleTurnChat:
      return 'singleTurnChatInputFile';
    case TaskTypeEnum.textGeneration:
      return 'textGenerationInputFile';
    case TaskTypeEnum.informationExtraction:
      return 'informationExtractionDatasetUpload';
    case TaskTypeEnum.conversationalAgent:
      return 'caDatasetUpload';
    case TaskTypeEnum.voiceAgent:
      return 'voiceAgentDatasetUpload';
    default:
      return null;
  }
};

const EMPTY_DOCUMENTS: DocumentsMap = new Map();

const FilePreviewBase: React.FC<FilePreviewProps> = ({
  source,
  filename,
  onClose,
  defaultLayout,
  audioRecordingIds,
  rowIds,
  initialRowId,
  documents,
  taskType,
}) => {
  const { datasetsApi } = useApi();
  const sourceRef = useRef(source);
  const blobRef = useRef<Blob | File | null>(null);

  const [state, setState] = useState<LoadState>({ status: 'loading' });
  const [query, setQuery] = useState('');
  const [page, setPage] = useState(0);
  const [reloadKey, setReloadKey] = useState(0);
  const [layout, setLayout] = useState<PreviewLayout>(defaultLayout ?? 'grid');
  const [cellView, setCellView] = useState<{ columnId: string; index: number } | null>(null);
  const [selectedRecord, setSelectedRecord] = useState(0);

  const key = infoboxKey(taskType);
  const infoHtml = key ? notionStore.values?.[key] : null;

  const ext = useMemo(() => filename.split('.').pop()?.toLowerCase() ?? '', [filename]);

  const cacheRef = useRef(new Map<string, DatasetAudioRecording>());

  const resolveAudioByFileName: ResolveAudioByFileName | null = useCallback(
    async (fileName: string) => {
      const cached = cacheRef.current.get(fileName);
      if (cached) return cached;

      const result = await datasetsApi.apiDatasetsAudioRecordingsList({ fileName });
      const recording = result.results?.[0] ?? null;
      if (recording) cacheRef.current.set(recording.fileName, recording);
      return recording;
    },
    [datasetsApi],
  );

  const resolverValue = resolveAudioByFileName;

  useEffect(() => {
    let isActive = true;
    setState({ status: 'loading' });

    const load = async () => {
      if (ext !== 'csv' && ext !== 'jsonl') {
        setState({ status: 'unsupported' });
        return;
      }

      let text: string;
      let bytes: number | null = null;
      try {
        const src = sourceRef.current;
        if (src instanceof File) {
          blobRef.current = src;
          bytes = src.size;
          text = await src.text();
        } else {
          const blob = await src();
          if (!isActive) return;
          blobRef.current = blob;
          bytes = blob.size;
          text = await blob.text();
        }
      } catch (error) {
        if (!isActive) return;
        setState({ status: 'error', fetchStatus: error instanceof FileFetchError ? error.status : null });
        return;
      }

      if (!isActive) return;
      if (bytes !== null && bytes > BYTE_TRIPWIRE) {
        setState({ status: 'error', fetchStatus: 'toolarge' });
        return;
      }

      const parsed = ext === 'csv' ? parseCsv(text, bytes) : parseJsonl(text, bytes);
      const analyses = analyzeColumns(parsed);
      setState({ status: 'ready', parsed, analyses });
      setLayout(defaultLayout ?? recommendLayout(analyses));
    };

    load();
    return () => {
      isActive = false;
    };
  }, [ext, defaultLayout, reloadKey]);

  useEffect(() => {
    setPage(0);
  }, [query]);

  const parsed = state.status === 'ready' ? state.parsed : null;
  const analyses = state.status === 'ready' ? state.analyses : [];

  // Rows in scope before search: all of them, or only those named by `rowIds`. Row
  // numbers stay the file's own, so a subset still points back into the full file.
  const scoped = useMemo(() => {
    if (!parsed) return [] as { row: Record<string, unknown>; number: number }[];
    const withNums = parsed.rows.map((row, i) => ({ row, number: i + 1 }));
    if (!rowIds) return withNums;
    const wanted = new Set(rowIds);
    return withNums.filter(({ row }) => wanted.has(String(row.id)));
  }, [parsed, rowIds]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!parsed || !q) return scoped;
    return scoped.filter(({ number }) => parsed.searchIndex[number - 1]?.includes(q));
  }, [parsed, scoped, query]);

  useEffect(() => {
    if (initialRowId === undefined) return;
    const index = scoped.findIndex(({ row }) => String(row.id) === initialRowId);
    if (index >= 0) setSelectedRecord(index);
  }, [scoped, initialRowId]);

  const pageCount = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const safePage = Math.min(page, pageCount - 1);
  const pageRows = filtered.slice(safePage * PAGE_SIZE, safePage * PAGE_SIZE + PAGE_SIZE);

  const download = () => {
    if (blobRef.current) GeneralHelper.saveFile(blobRef.current, filename);
  };

  const rowCount = rowIds ? `${scoped.length} flagged of ${parsed?.rows.length} rows` : `${parsed?.rows.length} rows`;
  const headerCounts = parsed && `${rowCount} · ${parsed.headers.length} columns · ${formatBytes(parsed.totalBytes)}`;

  const openCell = (localIndex: number, columnId: string) =>
    setCellView({ columnId, index: safePage * PAGE_SIZE + localIndex });

  const body = () => {
    if (state.status === 'loading') {
      return (
        <div className={styles.skeletonWrap}>
          {['38%', '100%', '92%', '96%', '64%'].map(w => (
            <div
              key={w}
              className={styles.skeleton}
              style={{ width: w }}
            />
          ))}
        </div>
      );
    }

    if (state.status === 'unsupported') {
      return (
        <div className={styles.center}>
          <div className={styles.centerTitle}>Preview isn’t available for .{ext} files</div>
          <p>CSV and JSONL files can be previewed here.</p>
        </div>
      );
    }

    if (state.status === 'error') {
      const { title, detail } = errorMessage(state.fetchStatus);
      return (
        <div className={styles.center}>
          <div className={styles.centerTitle}>{title}</div>
          <p>{detail}</p>
          <div className={styles.centerActions}>
            <button
              type="button"
              className={clsx(styles.btn, styles.btnOut)}
              onClick={() => setReloadKey(k => k + 1)}
            >
              Try again
            </button>
            {blobRef.current && (
              <button
                type="button"
                className={clsx(styles.btn, styles.btnGhost)}
                onClick={download}
              >
                Download instead
              </button>
            )}
          </div>
        </div>
      );
    }

    if (parsed && parsed.rows.length > 0 && scoped.length === 0) {
      return (
        <div className={styles.center}>
          <div className={styles.centerTitle}>None of the flagged rows were found in this file</div>
        </div>
      );
    }

    if (!parsed || parsed.rows.length === 0) {
      return (
        <div className={styles.center}>
          <div className={styles.centerTitle}>This file has headers but no rows</div>
          {parsed && parsed.headers.length > 0 && (
            <p>
              {parsed.headers.length} columns were found: {parsed.headers.join(', ')}.
            </p>
          )}
          {blobRef.current && (
            <div className={styles.centerActions}>
              <button
                type="button"
                className={clsx(styles.btn, styles.btnGhost)}
                onClick={download}
              >
                Download instead
              </button>
            </div>
          )}
        </div>
      );
    }

    return (
      <SearchContext.Provider value={query}>
        {layout === 'grid' ? (
          <PreviewGrid
            headers={parsed.headers}
            rows={pageRows.map(r => r.row)}
            rowNumbers={pageRows.map(r => r.number)}
            analyses={analyses}
            onOpenCell={openCell}
          />
        ) : (
          <RecordsView
            headers={parsed.headers}
            rows={filtered.map(r => r.row)}
            rowNumbers={filtered.map(r => r.number)}
            analyses={analyses}
            selectedIndex={Math.min(selectedRecord, Math.max(0, filtered.length - 1))}
            onSelect={setSelectedRecord}
          />
        )}
      </SearchContext.Provider>
    );
  };

  const cellRenderer = cellView ? getRenderer(analyses.find(a => a.id === cellView.columnId)?.kind ?? 'plain') : null;

  const documentsValue = documents ?? EMPTY_DOCUMENTS;

  return (
    <AudioRecordingsContext.Provider value={resolverValue}>
      <DocumentsContext.Provider value={documentsValue}>
        <OverlayShell onClose={onClose}>
          <div className={styles.head}>
            <FileText
              size={16}
              className={styles.fileIcon}
            />
            <strong className={styles.filename}>{filename}</strong>
            {headerCounts && <span className={styles.meta}>{headerCounts}</span>}
            <div className={styles.sp} />
            {infoHtml && (
              <Tooltip
                isFixed
                placement="bottom-end"
                content={
                  <div dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(infoHtml, { ADD_ATTR: ['target'] }) }} />
                }
              >
                <button
                  type="button"
                  className={styles.iconBtn}
                  aria-label="About this data"
                >
                  <HelpCircle size={18} />
                </button>
              </Tooltip>
            )}
            <button
              type="button"
              className={styles.iconBtn}
              aria-label="Close preview"
              onClick={onClose}
            >
              <X size={18} />
            </button>
          </div>

          {state.status === 'ready' && (
            <div className={styles.tools}>
              <div className={styles.search}>
                <Search
                  size={14}
                  className={styles.searchIcon}
                />
                <input
                  value={query}
                  onChange={e => setQuery(e.target.value)}
                  placeholder="Search all columns"
                  aria-label="Search rows"
                />
                {query && (
                  <button
                    type="button"
                    className={styles.iconBtn}
                    aria-label="Clear search"
                    onClick={() => setQuery('')}
                  >
                    <X size={13} />
                  </button>
                )}
              </div>
              {query && (
                <span className={styles.matchCount}>
                  Filtered to {filtered.length} of {scoped.length} rows
                </span>
              )}
              <div className={styles.sp} />
              <div className={styles.seg}>
                <button
                  type="button"
                  aria-pressed={layout === 'grid'}
                  onClick={() => setLayout('grid')}
                >
                  Grid
                </button>
                <button
                  type="button"
                  aria-pressed={layout === 'records'}
                  onClick={() => setLayout('records')}
                >
                  Records
                </button>
              </div>
            </div>
          )}

          {parsed?.truncated && (
            <div className={clsx(styles.banner, styles.bannerInfo)}>
              <Info size={15} />
              <div>
                Showing the first {parsed.rows.length} rows.{' '}
                <button
                  type="button"
                  className={styles.bannerLink}
                  onClick={download}
                >
                  Download the file
                </button>{' '}
                to see everything.
              </div>
            </div>
          )}

          {parsed && parsed.errors.length > 0 && (
            <div className={clsx(styles.banner, styles.bannerWarn)}>
              <AlertTriangle size={15} />
              <div>
                <b>{parsed.errors.length} rows could not be read.</b> The rest of the file is shown below.
              </div>
            </div>
          )}

          <div className={styles.bodyScroll}>{body()}</div>

          {state.status === 'ready' && parsed && scoped.length > 0 && (
            <div className={styles.foot}>
              {layout === 'grid' ? (
                <>
                  <span>
                    Showing {filtered.length} of {scoped.length} rows
                    {query && (
                      <>
                        {' · '}
                        <button
                          type="button"
                          className={styles.bannerLink}
                          onClick={() => setQuery('')}
                        >
                          clear search
                        </button>
                      </>
                    )}
                  </span>
                  {pageCount > 1 && (
                    <div className={styles.pager}>
                      <button
                        type="button"
                        disabled={safePage === 0}
                        onClick={() => setPage(safePage - 1)}
                      >
                        <ChevronLeft size={14} />
                      </button>
                      <span>
                        {safePage + 1} / {pageCount}
                      </span>
                      <button
                        type="button"
                        disabled={safePage >= pageCount - 1}
                        onClick={() => setPage(safePage + 1)}
                      >
                        <ChevronRight size={14} />
                      </button>
                    </div>
                  )}
                </>
              ) : (
                <span>
                  Record {Math.min(selectedRecord + 1, filtered.length)} of {filtered.length}
                </span>
              )}
            </div>
          )}
        </OverlayShell>

        {cellView && cellRenderer && parsed && (
          <CellFullView
            filename={filename}
            columnId={cellView.columnId}
            rows={filtered.map(r => r.row)}
            rowNumbers={filtered.map(r => r.number)}
            rowIndex={cellView.index}
            renderer={cellRenderer}
            onNavigate={index => setCellView({ columnId: cellView.columnId, index })}
            onClose={() => setCellView(null)}
          />
        )}
      </DocumentsContext.Provider>
    </AudioRecordingsContext.Provider>
  );
};

export const FilePreview = observer(FilePreviewBase);
